import React, { FC, useEffect, useState} from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { useStyles } from './styles'
import { CustomDataGrid, StatusTag } from '../../common'
import { useDispatch } from 'react-redux'
import { setSnackBar } from '../../../stores/ui/ui-action'
import { getAllSvbers } from '../../../services/bff-service'
import { UserListFilters } from './user-list-filters'
import { Box, debounce } from '@mui/material'
import moment from 'moment-timezone'
import { useCustomBackRouteNavigator } from '../../../shared/hooks/use-route-navigator'

export const UserList: FC<any> = () => {
    const styles = useStyles()
    const dispatch = useDispatch()

    const {
        searchParams: { tab = 'active' }
    } = useCustomBackRouteNavigator()

    const [internalUsers, setInternalUsers] = useState([]) // List of all users
    const [filteredUsers, setFilteredUsers] = useState([]) // Filtered list of users
    const [isLoading, setLoading] = useState(true)

    const [searchText, setSearchText] = useState('') // Search Text for filter users

    useEffect(() => {
        invokeGetUsersAPI()
    }, [])

    const invokeGetUsersAPI = () => {
        getAllSvbers()
            .then((data: any) => {
                setInternalUsers(data?.results)
            })
            .catch((error) => {
                dispatch(
                    setSnackBar({
                        show: true,
                        message: 'Error during setup, please refresh and try again momentarily.',
                        description: error.message,
                        messageType: 'error'
                    })
                )
            })
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        if (!searchText) {
            setFilteredUsers(internalUsers.filter(({ status }) => status === (tab === 'active')))
            return
        }
        //TO DO - till we get confirmation for search variable, search on userEmail, firstName, lastName
        const filteredRows = internalUsers.filter(({ firstName, lastName, userEmail, status }) => {
            return (
                status === (tab === 'active') &&
                (userEmail?.toLowerCase().includes(searchText.toLowerCase()) ||
                    firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
                    lastName?.toLowerCase().includes(searchText.toLowerCase()))
            )
        })
        setFilteredUsers(filteredRows)
    }, [internalUsers, searchText, tab])

    const columns: GridColDef[] = [
        {
            field: 'firstName',
            headerName: 'First Name',
            sortable: true,
            width: 228,
            headerClassName: styles.hideRightSeparator,
            renderCell: (params: GridRenderCellParams) => (params.value ? params.value : 'NA')
        },
        {
            field: 'lastName',
            headerName: 'Last Name',
            sortable: true,
            width: 200,
            headerClassName: styles.hideRightSeparator,
            renderCell: (params: GridRenderCellParams) => (params.value ? params.value : 'NA')
        },
        {
            field: 'userEmail',
            headerName: 'Email ID',
            sortable: true,
            flex: 1,
            minWidth: 242,
            headerClassName: styles.hideRightSeparator,
            renderCell: (params: GridRenderCellParams) => (params.value ? params.value : 'NA')
        },
        {
            field: 'roles',
            headerName: 'User Role',
            sortable: true,
            flex: 1,
            minWidth: 242,
            headerClassName: styles.hideRightSeparator,
            valueGetter: (params: GridRenderCellParams) => params.value?.join(',') ?? 'NA'
        },
        {
            field: 'status',
            headerName: 'Status',
            sortable: true,
            width: 148,
            headerClassName: styles.hideRightSeparator,
            renderCell: (params: GridRenderCellParams) => (
                <StatusTag caseName="clientStatus" status={params.value ? 'Active' : 'Inactive'} />
            )
        },
        {
            field: 'lastLogin',
            headerName: 'Last Login',
            sortable: true,
            width: 300,
            headerClassName: styles.hideRightSeparator,
            valueGetter: (params) =>
                params.value ? moment.tz(params?.value, 'ddd, MMMM D, YYYY, h:mm:ss A', 'America/Los_Angeles').valueOf() : new Date(),
            renderCell: (params: GridRenderCellParams) => (
                <>
                    {params.row.lastLogin
                        ? `${moment
                              .tz(params.row.lastLogin, 'ddd, MMMM D, YYYY, h:mm:ss A', 'America/Los_Angeles')
                              .format('MMM DD, YYYY h:mm:ss a')} PT`
                        : null}
                </>
            )
        },
        {
            field: 'createdOn',
            headerName: 'Creation Date',
            sortable: true,
            width: 228,
            headerClassName: styles.hideRightSeparator,
            valueGetter: (params) => moment.tz(params?.value, 'ddd, MMMM D, YYYY, h:mm:ss A', 'America/Los_Angeles').valueOf(),
            renderCell: (params: GridRenderCellParams) => (
                <>{moment.tz(params.row.createdOn, 'ddd, MMMM D, YYYY, h:mm:ss A', 'America/Los_Angeles').format('MMM DD, YYYY')}</>
            )
        }
    ]

    return (
        <>
            <Box className={styles.container}>
                <Box className={styles.paper}>
                    <UserListFilters defaultSearchText={searchText} onSearch={debounce(setSearchText, 300)} />
                </Box>
                <Box className={styles.paper}>
                    <CustomDataGrid
                        className={styles.userDataGridWrapper}
                        columns={columns}
                        rows={filteredUsers}
                        loading={isLoading}
                        getRowId={(row: any) => `${row.userId}`}
                    />
                </Box>
            </Box>
        </>
    )
}
